import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Modern Pagination Footer Component
 * 
 * A reusable footer component with pagination controls, similar to AutoERP system.
 * Features pagination checkbox, rows per page selector, page navigation, and total count.
 * 
 * LOCATION: /components/common/ModernPaginationFooter.jsx
 * USAGE: Import from "@/components/common/ModernPageTemplate" (re-exported)
 *        OR directly from "@/components/common/ModernPaginationFooter"
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isPaginationEnabled - Whether pagination is enabled
 * @param {Function} props.onPaginationToggle - Handler for pagination checkbox toggle
 * @param {number} props.rowsPerPage - Current rows per page value
 * @param {Function} props.onRowsPerPageChange - Handler for rows per page change
 * @param {Object} props.pagination - Pagination data from API
 * @param {number} props.pagination.current - Current page number
 * @param {number} props.pagination.pages - Total number of pages
 * @param {number} props.pagination.total - Total number of records
 * @param {Function} props.onPageChange - Handler for page navigation (also handles dropdown selection)
 * @param {Array} props.rowsOptions - Available options for rows per page (default: [5, 10, 20, 50])
 */
const ModernPaginationFooter = ({
  isPaginationEnabled,
  onPaginationToggle,
  rowsPerPage,
  onRowsPerPageChange,
  pagination = {},
  onPageChange,
  rowsOptions = [5, 10, 20, 50]
}) => {
  const { current = 1, pages = 1, total = 0 } = pagination;

  return (
    <div className="admin-fixed-footer">
      <div className="admin-footer-content">
        {/* Left side - Pagination checkbox and controls */}
        <div className="admin-footer-left">
          <div className="admin-footer-pagination-checkbox">
            <input
              type="checkbox"
              id="pagination-toggle"
              checked={isPaginationEnabled}
              onChange={(e) => onPaginationToggle(e.target.checked)}
              className="admin-footer-checkbox"
            />
            <label htmlFor="pagination-toggle" className="admin-footer-pagination-indicator">
              Pagination
            </label>
          </div>
          
          {isPaginationEnabled && (
            <div className="admin-footer-rows">
              <span>Rows:</span>
              <Select value={rowsPerPage.toString()} onValueChange={onRowsPerPageChange}>
                <SelectTrigger className="admin-footer-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rowsOptions.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Center - Pagination controls (always show when pagination is enabled) */}
        <div className="admin-footer-center">
          {isPaginationEnabled && (
            <>
              <button
                className={`admin-footer-btn ${current <= 1 ? 'disabled' : ''}`}
                onClick={() => onPageChange(current - 1)}
                disabled={current <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                let pageNum;
                
                if (pages <= 5) {
                  pageNum = i + 1;
                } else {
                  if (current <= 3) {
                    pageNum = i + 1;
                  } else if (current >= pages - 2) {
                    pageNum = pages - 4 + i;
                  } else {
                    pageNum = current - 2 + i;
                  }
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`admin-footer-page-btn ${current === pageNum ? 'active' : ''}`}
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className={`admin-footer-btn ${current >= pages ? 'disabled' : ''}`}
                onClick={() => onPageChange(current + 1)}
                disabled={current >= pages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Right side - Go to page and total */}
        <div className="admin-footer-right">
          {isPaginationEnabled && (
            <div className="admin-footer-goto">
              <span>Go to:</span>
              <Select 
                value={current.toString()} 
                onValueChange={(value) => onPageChange(parseInt(value))}
              >
                <SelectTrigger className="admin-footer-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: Math.max(pages, 1) }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <span className="admin-footer-total">
            Total: {total || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ModernPaginationFooter;